// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { _x } from '@wordpress/i18n';

// Import components
import './button-action';

// Import style for web component
import styles from './web-component-style.scss?inline';

/**
 * Web component representing a group of addons
 *
 * @augments LitElement
 */
@customElement( 'uap-page-addons-group-item' ) // eslint-disable-line require-jsdoc
export class AutomatorPageAddonsGroupItem extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * The addon to display.
	 *
	 * @type {Addon}
	 */
	@property( { type: Object, attribute: false } )
	accessor addon;

	/**
	 * Whether the user has access to the addon.
	 *
	 * @type {Boolean}
	 */
	@property( { type: Boolean, attribute: 'has-access' } )
	accessor hasAccess = false;

	/**
	 * Renders the component
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	render() {
		return html`
			<div 
				class="item"
				?has-access=${ this.hasAccess }
			>
				${ this._templateIcon }
				${ this._templateContent }
				${ this._templateCtas }
			</div>
		`;
	}

	/**
	 * Renders the icon
	 *
	 * @return {TemplateResult} The template
	 */
	get _templateIcon() {
		return html`
			<div class="item-icon">
				<uo-icon id="${ this.addon.icon }"></uo-icon>
			</div>
		`;
	}

	/**
	 * Renders the content
	 *
	 * @return {TemplateResult} The template
	 */
	get _templateContent() {
		return html`
			<div class="item-content">
				<div class="item-content-top">
					${ this._templateName }
					${ this._templateDeveloper }
				</div>

				<div class="item-content-description">
					${ this.addon.description }
				</div>

				${ this._templateDetails }
			</div>
		`;
	}

	/**
	 * Renders the details
	 *
	 * @return {TemplateResult} The template
	 */
	get _templateDetails() {
		return html`
			<table class="item-content-details">
				<tbody>
					${ repeat(
						this.addon.details,
						detail => detail.label,
						detail => this._templateDetail( detail )
					) }
				</tbody>
			</table>
		`;
	}

	/**
	 * Renders a detail
	 *
	 * @param {AddonDetail} detail - The detail to render
	 * @return {TemplateResult} The template
	 */
	_templateDetail( detail ) {
		return html`
			<tr class="item-content-detail">
				<td class="item-content-detail-label">
					${ detail.label }
				</td>
				<td class="item-content-detail-value">
					<uo-markdown
						allowed-tags="strong,em,a"
						.content=${ detail.value }
					></uo-markdown>
				</td>
			</tr>
		`;
	}

	/**
	 * Renders the name
	 *
	 * @return {TemplateResult} The template
	 */
	get _templateName() {
		return html`
			<div class="item-content-name">
				${ this.addon.name }
			</div>
		`;
	}

	/**
	 * Renders the developer
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	get _templateDeveloper() {
		return html`
			<div class="item-content-developer">
				${ escapeHTML( _x( 'By', 'Addons', 'uncanny-automator' ) ) }

				<a href="${ this.addon.developer.url }" target="_blank">
					${ this.addon.developer.name } 
					${ this.addon.is_official ? html`<uo-icon id="badge-check" icon-style="solid"></uo-icon>` : '' }
					<uo-icon id="external-link" icon-style="solid"></uo-icon>
				</a>
			</div>
		`;
	}

	/**
	 * Renders the call-to-action buttons
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	get _templateCtas() {
		if ( isEmpty( this.addon.cta ) ) {
			return;
		}

		return html`
			<div class="item-ctas">
				${ repeat(
					this.addon.cta,
					cta => cta.label,
					cta => this._templateCtaButton( cta )
				) }
			</div>
		`;
	}

	/**
	 * Renders a call-to-action button
	 *
	 * @param {AddonCTA} cta - The call-to-action button to render
	 * @return {TemplateResult} The template
	 */
	_templateCtaButton( cta ) {
		if ( cta.action ) {
			return html`
				<uap-page-addons-group-item-button-action
					addon-id=${ this.addon.id }
					.cta=${ cta }

					@automator-page-addons-group-item-data-changed=${ this.handleAddonDataChanged }
				></uap-page-addons-group-item-button-action>
			`;
		}

		return html`
			<uo-button 
				color=${ cta.type }
				href=${ cta.disabled ? '' : cta.url }
				target=${ cta.disabled ? '' : '_blank' }
				?disabled=${ cta.disabled }
			>
				${ cta.icon ? html`<uo-icon id="${ cta.icon }"></uo-icon>` : '' }
				${ cta.label }
			</uo-button>
		`;
	}

	/**
	 * Handles the addon data changed event
	 *
	 * @param {CustomEvent} event - The event
	 */
	handleAddonDataChanged( event ) {
		// Get the addon
		const addon = event.detail.addon;

		// Update the addon
		this.addon = addon;
	}

}